import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';

import compact from 'lodash/compact';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import flatMap from 'lodash/flatMap';
import flatten from 'lodash/flatten';
import forOwn from 'lodash/forOwn';
import forEach from 'lodash/forEach';
import has from 'lodash/has';
import reduce from 'lodash/reduce';
import times from 'lodash/times';
import intersection from 'lodash/intersection';
import countBy from 'lodash/countBy';
import cloneDeep from 'lodash/cloneDeep';
import map from 'lodash/map';
import includes from 'lodash/includes';
import listensToClickOutside from 'react-onclickoutside';
import classNames from 'classnames/bind';
import ExpansionPanel from 'zignal-landscape/lib/ExpansionPanel';
import ToolTip from 'zignal-landscape/lib/ToolTip';
import ExpansionPanelBody from 'zignal-landscape/lib/ExpansionPanelBody';
import ExpansionPanelTitle from 'zignal-landscape/lib/ExpansionPanelTitle';
import Button from 'zignal-landscape/lib/Button';
import ToggleSwitch from 'zignal-landscape/lib/ToggleSwitch';

import WorkbenchField from './WorkbenchField.js';
import styles from './WorkbenchPanel.css';
import DuplicateCodingMenu from './DuplicateCodingMenu.js';

const USER_CREATED_CLASS_NAME = 'Select-create-option-placeholder';
const HEADERS = {
  'Accept': 'application/json',
  'Content-Type': 'application/json'
};

const SENTIMENT_FIELD = {
  id: 'Sentiment',
  name: 'Sentiment',
  type: 'Sentiment',
  values: [
    {name: 'Very Negative', _id: -3},
    {name: 'Negative', _id: -2},
    {name: 'Slightly Negative', _id: -1},
    {name: 'Neutral', _id: 0},
    {name: 'Slightly Positive', _id: 1},
    {name: 'Positive', _id: 2},
    {name: 'Very Positive', _id: 3}
  ]
};

function _isUserCreatedValue(data) {
  return includes(data.className, USER_CREATED_CLASS_NAME);
}

export class WorkbenchPanel extends React.Component {

  constructor(props) {
    super(props);
    const { profiles, values, hitHighlightOn } = this.props;

    this.handleSelectedIndexChanged = this.handleSelectedIndexChanged.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleProfileClick = this.handleProfileClick.bind(this);
    this.focusNextField = this.focusNextField.bind(this);
    this.focusPreviousField = this.focusPreviousField.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.checkScrollClick = this.checkScrollClick.bind(this);
    this.getInitialSelectedTextValues = this.getInitialSelectedTextValues.bind(this);
    this.getCurrentField = this.getCurrentField.bind(this);
    this.getCurrentProfile = this.getCurrentProfile.bind(this);
    this.handleMultiSelectFieldEnter = this.handleMultiSelectFieldEnter.bind(this);
    this.sendOnSubmit = this.sendOnSubmit.bind(this);
    this.setClickOutside = this.setClickOutside.bind(this);
    this.handleToggleProfiles = this.handleToggleProfiles.bind(this);
    this.onDuplicateCodingSubmit = this.onDuplicateCodingSubmit.bind(this);

    this.panelRef = React.createRef();
    this.submitButtonRef = React.createRef();

    // Map values to initial UI state
    const selectedValueIndices = [];
    let fields = [];
    profiles.forEach((profile) => {
      const sentimentField = profile.isSubscribedDataset
        ? { ...SENTIMENT_FIELD, disabled: true }
        : SENTIMENT_FIELD;
      profile.fields = [sentimentField, ...profile.fields];
      times(profile.fields.length, () => selectedValueIndices.push(0));
      fields = fields.concat(profile.fields);
    });
    // Setup a map of all profiles, fields, and values for better tracking of selected, versus clicked on values
    const multiSelectedValues = {profiles: {}};

    let initialSelectedValueIndexForProfile = 0;
    profiles.forEach((profile) => {
      const valueObject = find(values, {profile_id: profile.id});
      const customValues = valueObject ? valueObject.custom_values : [];

      let sentimentValue;
      // we check that the path exist instead of check the object due to
      // Neutral Sentiment value [0] will be considered false
      if(valueObject && has(valueObject, 'sentiment_score_override')) {
        sentimentValue = parseInt(valueObject.sentiment_score_override, 10);
      }else{
        sentimentValue = parseInt(props.originalSentiment, 10);
      }

      const sentimentIndex = findIndex(SENTIMENT_FIELD.values, {_id: sentimentValue});
      if (sentimentIndex !== -1) {
        selectedValueIndices[initialSelectedValueIndexForProfile] = sentimentIndex + 1;
      }

      multiSelectedValues.profiles[profile.id] = {fields: {}};
      profile.fields.forEach((field, fi) => {
        const multiSelectedFields = multiSelectedValues.profiles[profile.id].fields[field.id] = {values: {}};
        const i = initialSelectedValueIndexForProfile + fi;
        field.values.forEach((value, index) => {
          multiSelectedFields.values[value._id] = {
            value,
            index: index + 1,
            selected: customValues.indexOf(value._id) !== -1
          };
        });

        customValues.forEach((value) => {
          const valueIndex = findIndex(field.values, {_id: value});
          if (valueIndex !== -1 && field.type !== 'Text') {
            selectedValueIndices[i] = valueIndex + 1;
          }
        });
      });
      initialSelectedValueIndexForProfile += profile.fields.length;
    });

    const firstScoringProfile = profiles.find((p) => !p.isSubscribedDataset) || profiles[0];
    const fieldsForFirstProfile = map(fields, (field) => {
      if (field[firstScoringProfile.campaignId]) {
        return field[firstScoringProfile.campaignId];
      }
      return field;
    });
    const selectedTextValues = this.getInitialSelectedTextValues(fieldsForFirstProfile, values);

    //TODO: Refactor selectedValueIndices to object for easier tracking
    //Something like:
    // selectedValuesByProfile = {
    //   profileId1: {
    //     valueId1: false,
    // ...

    let showAllProfilesLocalStorage =
      window.localStorage ? window.localStorage.getItem('workbench_show_all_profiles_enabled'): false;
    if (showAllProfilesLocalStorage && showAllProfilesLocalStorage === 'true') {
      showAllProfilesLocalStorage = true;
    } else {
      showAllProfilesLocalStorage = false;
    }
    const showAllProfiles = showAllProfilesLocalStorage || this.props.showAllProfiles;

    this.state = {
      fields: fieldsForFirstProfile,
      submitButtonFocused: false,
      focusedFieldIndex: 0,
      selectedValueIndices,
      multiSelectedValues,
      selectedTextValues,
      valuesModified: false, // For confirmation modal in the manual_scoring_modal Backbone view
      panelsOpen: profiles.map((profile, index) => {
        // Always start with first profile open
        if (index === 0) {
          return true;
        }
        return false;
      }),
      activeProfile: profiles[0],
      hitHighlightOn,
      showAllProfiles
    };
  }

  UNSAFE_componentWillMount() {
    const { dispatcher } = this.props;
    // listen to event from backbone to know when hithighlight is on
    dispatcher.on('hithighlight_toggle', (toggle) => {
      this.setState({hitHighlightOn: toggle});
    });
  }

  componentWillUnmount() {
    const { dispatcher } = this.props;
    dispatcher.off('hithighlight_toggle');
  }

  onDuplicateCodingSubmit(sourceProfile, profileIds) {
    const { onSubmit } = this.props;
    this.prepareOverrides((selectedValues) => {
      // Since selectedValues is an array on which the only entry corresponds to the profile that
      // was the source for coding copy then we will be copying copying it and repacibg only the profile_id
      const sourceProfileData = selectedValues.filter((v) => v.profile_id === sourceProfile)[0];
      const copiedValues = profileIds.map((profileId) => {
        // Cloning using JSON.parse and JSON.stringify to get a deep clone working properly
        const newProfile = cloneDeep(sourceProfileData);
        newProfile.profile_id = profileId;
        if (newProfile.sentiment_overrides[0]) {
          newProfile.sentiment_overrides[0].profile_id = profileId;
        }
        return newProfile;
      });
      copiedValues.push(sourceProfileData);
      onSubmit(copiedValues);
      this.setState({
        valuesModified: false // For confirmation modal in the manual_scoring_modal Backbone view
      });
    });
  }

  getInitialSelectedTextValues(fields, selectedValues) {
    const textFields = fields.filter((f) => f.type === 'Text');
    const possibleTextValues = flatMap(textFields, 'values');
    const possibleTextValueIds = map(possibleTextValues, '_id');

    const initialSelectedTextValues = selectedValues.reduce((aggregate, profileValues) => {
      const selectedTextValueIds = intersection(possibleTextValueIds, profileValues.custom_values);
      const selectedTextValues = map(selectedTextValueIds, (valueId) => {
        return find(possibleTextValues, { _id: valueId });
      });

      if(selectedTextValues.length > 0) {
        aggregate[profileValues.profile_id] = selectedTextValues;
      }
      return aggregate;
    }, {});

    return initialSelectedTextValues;
  }

  getProfileIndex(profile) {
    const { profiles } = this.props;
    let profileIndex = -1;
    profiles.forEach((p, pi) => {
      if (profile.id === p.id) {
        profileIndex = pi;
      }
    });
    return profileIndex;
  }

  getCurrentField() {
    const { fields } = this.state;
    const { focusedFieldIndex } = this.state;
    return fields[ focusedFieldIndex % fields.length ];
  }

  getCurrentProfile() {
    const { profiles } = this.props;
    const openPanelIndex = findIndex(this.state.panelsOpen, (panelOpen) => { return panelOpen; }) || 0;
    return profiles[openPanelIndex];
  }

  setClickOutside(valuesModified, outsideModal) {
    this.setState({ valuesModified });
    this.props.onClickedOutside(valuesModified, outsideModal);
  }

  // TODO: Update logic when migrating to React 17
  // eslint-disable-next-line camelcase

  handleFocus() {
    const maxIndex = this.state.fields.length;
    const newPanelsOpen = this.state.panelsOpen.slice();
    if (this.state.focusedFieldIndex === -1 ) {
      newPanelsOpen[0] = true;
      this.setState({focusedFieldIndex: 0, panelsOpen: newPanelsOpen});
    } else if (this.state.focusedFieldIndex === maxIndex) {
      newPanelsOpen[newPanelsOpen.length - 1] = true;
      this.setState({focusedFieldIndex: maxIndex - 1, panelsOpen: newPanelsOpen});
    }
  }

  // Allows Categorical and Text  fields
  // to be open while clicking the scroll bar
  checkScrollClick(e, clickedField){
    const clickedFieldXPosition = ReactDOM.findDOMNode(this).getBoundingClientRect().x;
    let clickXIndex = 100;
    let allowedField = false;
    if (e.clientX !== undefined) {
      clickXIndex = e.clientX;
    }
    if (clickedField !== undefined) {
      allowedField = clickedField.type === 'Categorical' || clickedField.type === 'Text';
    }
    const clickDiff = clickXIndex/clickedFieldXPosition;
    const target = e.target || e.srcElement;
    if (allowedField & clickDiff > 1.2 & target.className !== 'modal-backdrop fade in'){
      return true;
    }
    return false;
  }

  // This method is called by `listensToClickOutside` higher order component
  // to ensure currently opened field is closed by clicking outside WorkbenchPanel.
  handleClickOutside(e) {
    if (e)  {
      const clickedField = this.getCurrentField();
      //Check if clicked field allows scrollbar click without closing
      if (!this.checkScrollClick(e, clickedField)) {
        // For confirmation modal in the manual_scoring_modal Backbone view
        const target = e.target || e.srcElement;
        const clickedOutsideConfirmationModal = (target && target.className === 'modal-backdrop fade in');
        this.props.onClickedOutside(this.state.valuesModified, clickedOutsideConfirmationModal);

        this.setState({
          focusedFieldIndex: -1,
          focusedFieldIsOpen: false
        });
      }
    }
    else{
      this.setState({
        focusedFieldIndex: -1,
        focusedFieldIsOpen: false
      });
    }
  }

  sendOnSubmit() {
    const { onSubmit } = this.props;

    this.prepareOverrides((selectedValues) => {
      onSubmit(selectedValues);
      this.setState({
        valuesModified: false // For confirmation modal in the manual_scoring_modal Backbone view
      });
    });
  }

  prepareOverrides(cb) {
    // Map selectedValueIndices to [{"profile_id": profile_id, "custom_values": [value1_id, value2_id], .... }]
    const { profiles } = this.props;
    const { selectedValueIndices } = this.state;

    // generate values for any potential user created text values first
    this.createTextValues().then(() => {
      const { selectedTextValues } = this.state;
      let initialSelectedValueIndexForProfile = 0;
      const selectedValues = profiles.map((profile) => {
        const customValues = profile.fields.map((field, fi) => {
          if (field.type === 'Sentiment') {
            // ignore sentiment field
            return undefined;
          }
          const i = initialSelectedValueIndexForProfile + fi;
          const selectedValueIndex = selectedValueIndices[i];

          if (field.multi) {
            const multiSelectedValues = [];
            forOwn(this.state.multiSelectedValues.profiles[profile.id].fields[field.id].values, (selectedValue) => {
              if (selectedValue.selected) {
                multiSelectedValues.push(selectedValue.value._id);
              }
            });
            return multiSelectedValues;
          }
          return selectedValueIndex > 0 ? field.values[selectedValueIndex - 1]._id : undefined;
        });

        const customTextValues = map(selectedTextValues[profile.id], '_id');

        const submitData = {
          profile_id: profile.id,
          custom_values: compact(flatten(customValues)).concat(customTextValues),
          sentiment_overrides: []
        };

        const sentimentFieldIndex = initialSelectedValueIndexForProfile;
        const sentimentValueIndex  = selectedValueIndices[sentimentFieldIndex];
        if (sentimentValueIndex > 0) {
          submitData.sentiment_overrides.push({
            profile_id: profile.id,
            sentiment_score: SENTIMENT_FIELD.values[sentimentValueIndex - 1]._id
          });
        }
        initialSelectedValueIndexForProfile += profile.fields.length;
        return submitData;
      });

      cb(selectedValues);
    });
  }

  handleSelectedIndexChanged(fieldIndex, valueIndex, keepOpen) {
    const selectedValueIndices = this.state.selectedValueIndices;
    selectedValueIndices[fieldIndex] = valueIndex;

    this.setState({
      selectedValueIndices,
      focusedFieldIsOpen: keepOpen,
      valuesModified: true
    });

    this.props.onClickedOutside(this.state.valuesModified, false);

    if(!keepOpen) {
      this.panelRef.current.focus();
    }
  }
  /**
  * This method overwrites the the text values
  * for the given field within the given profile
  */
  handleSelectedTextChanged(textValues, profileId, fieldId) {
    const oldSelectedTextValues = this.state.selectedTextValues;
    let newSelectedTextValues = [];

    if( oldSelectedTextValues[profileId] ) {
      newSelectedTextValues = oldSelectedTextValues[profileId].filter((value) => {
        return value.workbenchFieldId !== fieldId;
      });
    }

    // add the workbench field id to any user created values
    // textValues = textValues.map((value) => {
    // textValues = textValues.map((value) => {
    forEach(textValues, (value) => {
      if(!value.workbenchFieldId) {
        value.workbenchFieldId = fieldId;
      }
    });

    newSelectedTextValues = textValues.concat(newSelectedTextValues);
    this.setState({
      selectedTextValues: { ...oldSelectedTextValues, [profileId]: newSelectedTextValues },
      valuesModified: true
    });

    this.props.onClickedOutside(this.state.valuesModified, false);
  }

  /**
   * This method is called before onSubmit
   * and saves selectedTextValues to the state
   */
  createTextValues() {
    const { selectedTextValues } = this.state;
    const valuesToGenerate = [];
    const dedupeCache = {};

    // first determine which values need to be generated,
    // and only generate on per value name (keep track of them in the dedupeCache)
    forOwn(selectedTextValues, (values) => {
      forEach(values, (val) => {
        if(val.hasOwnProperty('__isNew__')) {
          val.className = USER_CREATED_CLASS_NAME;
        }
        if(_isUserCreatedValue(val) && !dedupeCache[val.name]) {
          dedupeCache[val.name] = val;
          valuesToGenerate.push(val);
        }
      });
    });

    return this.generateUserCreatedTextValues(valuesToGenerate).then((generatedTextValues) => {
      const textValues = reduce(selectedTextValues, (result, values, profileId) => {
        const textValuesByProfile = values.map((val) => {
          if(_isUserCreatedValue(val)) {
            return find(generatedTextValues, {name: val.name});
          }
          return val;
        });

        result[profileId] = textValuesByProfile;
        return result;
      }, {});

      this.setState({
        selectedTextValues: textValues
      });
    });
  }

  /**
   * This method takes an array of user created text values, makes http
   * requests to generate them, and then returns them in an array
   */
  generateUserCreatedTextValues(textValues) {
    const {
      accountId,
      campaignId,
      customFields,
    } = this.props;
    const customFieldsById = customFields.reduce((mapping, customField) => {
      mapping[customField.id] = customField;
      return mapping;
    }, {});

    const httpCalls = textValues.map((newValue) => {
      const fieldId = newValue.workbenchFieldId;
      const customField = customFieldsById[fieldId];

      let url;
      if (customField.isAvailableToAccount) {
        url = `/api/accounts/${accountId}/workbench_fields/${fieldId}/values`;
      }
      else {
        url = `/api/campaigns/${campaignId}/workbench_fields/${fieldId}/values`;
      }

      return window.fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: new window.Headers(HEADERS),
        body: JSON.stringify({ name: newValue.name })
      })
        .then((response) => {
          return response.json();
        });
    });

    return Promise.all(httpCalls);
  }

  handleMultiSelectFieldEnter () {

    const currentField = this.getCurrentField();
    const currentProfile = this.getCurrentProfile();

    const state = JSON.parse(JSON.stringify(this.state.multiSelectedValues));
    const selectedState = state.profiles[currentProfile.id].fields[currentField.id];
    const selectedValueIndices = this.state.selectedValueIndices.slice();
    const currentValueIndex = selectedValueIndices[this.state.focusedFieldIndex];

    if (currentValueIndex !== 0 && currentValueIndex !== currentField.values.length + 1) {
      const selectedValue = find(selectedState.values, {index: currentValueIndex});
      selectedValue.selected = !selectedValue.selected;

      this.setState({
        multiSelectedValues: state,
        focusedFieldIsOpen: true
      });

      // Highlight the next value option on enter
      this.nextFieldValue();
    }
    else {
      this.setState({
        selectedValueIndices,
        focusedFieldIsOpen: !this.state.focusedFieldIsOpen
      });
    }
  }

  handleKeyDown(e) { // eslint-disable-line complexity
    if (e.defaultPrevented) {
      return;
    }

    let propagate = false;
    const { submitButtonFocused } = this.state;
    const currentField = this.getCurrentField();
    switch (e.key) {
      case 'Escape':
        this.setClickOutside(true, true);
        break;
      case 'ArrowDown':
        if (this.state.focusedFieldIsOpen) {
          this.nextFieldValue();
        } else {
          this.focusNextField();
        }
        break;
      case 'ArrowUp':
        if (this.state.focusedFieldIsOpen) {
          this.previousFieldValue();
        } else {
          this.focusPreviousField();
        }
        break;
      case 'ArrowRight':
        this.nextFieldValue();
        this.setClickOutside(true, false);
        break;
      case 'ArrowLeft':
        this.previousFieldValue();
        this.setClickOutside(true, false);
        break;
      case 'Enter':
        this.setClickOutside(true, false);
        if (submitButtonFocused) {
          this.sendOnSubmit();
        }
        else if (currentField && currentField.multi) {
          this.handleMultiSelectFieldEnter();
        }
        else if (currentField && currentField.type === 'Text') {
          if(this.state.focusedFieldIsOpen) {
            // hack to prevent panel from losing focus when text field is closed
            this.panelRef.current.focus();
          }
          this.setState({focusedFieldIsOpen: !this.state.focusedFieldIsOpen});
        }
        else {
          this.setState({focusedFieldIsOpen: !this.state.focusedFieldIsOpen});
        }
        break;
      default:
        propagate = true;
        break;
    }

    if (!propagate) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  nextFieldValue() {
    const {
      selectedValueIndices,
      focusedFieldIndex,
      fields
    } = this.state;

    const newSelectedValueIndices = selectedValueIndices.slice();
    const currentValue = newSelectedValueIndices[focusedFieldIndex];
    const currentField = this.getCurrentField();
    let maxValue = fields[focusedFieldIndex % fields.length].values.length;

    if (currentField && currentField.multi) {
      maxValue = fields[focusedFieldIndex % fields.length].values.length + 1;
    }

    newSelectedValueIndices[focusedFieldIndex] = Math.min(currentValue + 1, maxValue);
    this.setState({selectedValueIndices: newSelectedValueIndices});
  }

  previousFieldValue() {
    const selectedValueIndices = this.state.selectedValueIndices.slice();
    const currentValue = selectedValueIndices[this.state.focusedFieldIndex];
    selectedValueIndices[this.state.focusedFieldIndex] = Math.max(0, currentValue - 1);
    this.setState({selectedValueIndices});
  }

  focusNextField() {
    const { profiles } = this.props;
    const { fields, activeProfile } = this.state;

    const maxIndex = this.state.fields.length;
    const activeProfileIndex = this.getProfileIndex(activeProfile);

    const nextIndex = Math.min(
      this.state.focusedFieldIndex + 1,
      maxIndex
    );

    const newPanelsOpen = this.state.panelsOpen.slice();
    if (nextIndex < maxIndex) {
      let nextIndexThatRequiresOpeningNewPanel = 0;
      profiles.forEach((profile, pi) => {
        nextIndexThatRequiresOpeningNewPanel += profile.fields.length;
        if (nextIndex >= nextIndexThatRequiresOpeningNewPanel) {
          if (newPanelsOpen[pi+1] === undefined) {
            newPanelsOpen[pi] = true;
          } else {
            newPanelsOpen[pi+1] = true;
          }
        }
      });
    }

    if ((nextIndex % fields.length === 0) && (nextIndex !== maxIndex)) {
      this.setState({activeProfile: profiles[activeProfileIndex]});
      this.props.onProfileChange(profiles[activeProfileIndex]);
    }

    let submitButtonFocused = false;

    if (nextIndex === maxIndex) {
      submitButtonFocused = true;
      newPanelsOpen[newPanelsOpen.length - 1] = true;
    }

    this.setState({
      focusedFieldIndex: nextIndex,
      focusedFieldIsOpen: false,
      panelsOpen: newPanelsOpen,
      submitButtonFocused
    });

    if (submitButtonFocused) {
      this.submitButtonRef.current.focus();
    } else {
      this.panelRef.current.focus();
    }
  }

  focusPreviousField() {
    const { profiles } = this.props;
    const { fields, activeProfile, focusedFieldIndex } = this.state;

    const previousIndex = Math.max(focusedFieldIndex - 1, 0);
    const activeProfileIndex = this.getProfileIndex(activeProfile);
    const newPanelsOpen = this.state.panelsOpen.slice(activeProfile);
    const currentField = fields[focusedFieldIndex];

    if (previousIndex > -1 && currentField && currentField.type === 'Sentiment') {
      newPanelsOpen[activeProfileIndex-1] = true;
    }

    if (previousIndex % fields.length === (fields.length -1)) {
      this.setState({activeProfile: profiles[activeProfileIndex]});
      this.props.onProfileChange(profiles[activeProfileIndex]);
    }

    this.setState({
      focusedFieldIndex: previousIndex,
      focusedFieldIsOpen: false,
      panelsOpen: newPanelsOpen,
      submitButtonFocused: false
    });
    this.panelRef.current.focus();
  }

  handleFieldClick(fieldIndex, profile, event) {
    //Scroll to element to provide a proper visibility of its items and avoid hidden by scrolling
    event.currentTarget.scrollIntoView();

    if(!this.state.activeProfile || this.state.activeProfile.id !== profile.id) {
      this.setState({activeProfile: profile});
      this.props.onProfileChange(profile);
    }

    if (this.state.focusedFieldIndex === 0) {
      this.setClickOutside(true, false);
    }

    const newPanelsOpen = this.state.panelsOpen.slice();
    // newPanelsOpen[Math.floor(fieldIndex / this.state.fields.length)] = true;
    this.setState({focusedFieldIndex: fieldIndex, focusedFieldIsOpen: true, panelsOpen: newPanelsOpen});
  }

  handleProfileClick(profileIndex, profile) {
    const { fields } = this.state;
    if(!this.state.activeProfile || this.state.activeProfile.id !== profile.id) {
      this.setState({activeProfile: profile, fields});
      this.props.onProfileChange(profile);
    }

    const newPanelsOpen = this.state.panelsOpen.slice();
    newPanelsOpen[profileIndex] = !newPanelsOpen[profileIndex];
    const panels = countBy(newPanelsOpen);
    // don't allow user to close the last open panel
    if(!panels.true) {
      return;
    }
    this.setState({panelsOpen: newPanelsOpen});
  }

  handleToggleProfiles(e) {
    window.localStorage.setItem('workbench_show_all_profiles_enabled', e.target.checked);
    this.setState({showAllProfiles: e.target.checked});
  }

  render() {
    const { campaignId, toggleSubmitButton, hasAccountLevelCustomAttributesFeatureFlag } = this.props;
    const { selectedTextValues, activeProfile, hitHighlightOn, showAllProfiles } = this.state;
    let { profiles } = this.props;

    // don't render profiles outside campaign if show all profiles is false or feature flag isn't enabled
    if (showAllProfiles === false || hasAccountLevelCustomAttributesFeatureFlag === false) {
      profiles = profiles.filter((profile) => {
        return profile.isProfileOutsideActiveCampaign === false;
      });
    }
    let fieldIndexPosition = 0;
    const Profiles = profiles.map(
      (profile, pi) => {
        const Fields = profile.fields.map(
          (field, fi) => {
            const i = fieldIndexPosition + fi;
            const isFocusedField = this.state.focusedFieldIndex === i;
            return (
              <div
                key={i}
                onClick={(e) => {e.stopPropagation(); this.handleFieldClick(i, profile, e);}}
              >
                <WorkbenchField
                  isFocused={isFocusedField}
                  isOpen={isFocusedField && this.state.focusedFieldIsOpen}
                  field={field}
                  selectedIndex={this.state.selectedValueIndices[i]}
                  selectedValueIndices={this.state.selectedValueIndices}
                  onSelectedIndexChanged={
                    (newIndex, keepOpen) => {
                      this.handleSelectedIndexChanged(i, newIndex, keepOpen);
                    }
                  }
                  multiSelectedValues={this.state.multiSelectedValues.profiles[profile.id].fields[field.id].values}
                  onSelectedTextChanged={
                    (selectedValues) => {
                      this.handleSelectedTextChanged(selectedValues, profile.id, field.id);
                    }
                  }
                  campaignId={campaignId}
                  initiallySelectedTextValues={selectedTextValues[profile.id]}
                  toggleSubmitButton={toggleSubmitButton}
                  focusNextField={this.focusNextField}
                  focusPreviousField={this.focusPreviousField}
                />
              </div>
            );
          }
        );
        fieldIndexPosition += profile.fields.length;
        let profileTitle = profile.name;
        let expansionPanelTitleStyles = styles.expansionPanel;
        const activeProfileStyles = activeProfile.id === profile.id && hitHighlightOn ? styles.activeProfile : null;
        if (profile.isProfileOutsideActiveCampaign) {
          profileTitle = (<div className={styles.titleContainer}>
            <div className={styles.profileName}>{profile.name}</div>
            <div className={styles.campaignTitle}>{profile.campaignTitle}</div></div>);
          expansionPanelTitleStyles = classNames(expansionPanelTitleStyles, styles.otherProfileBars);
        }
        return (
          <div
            className={expansionPanelTitleStyles}
            key={profile.id}
            tabIndex="0"
            onClick={(e) => {e.stopPropagation(); this.handleProfileClick(pi, profile);}}
            onFocus={(e) => {e.stopPropagation();}}
          >
            <ExpansionPanel>
              <ExpansionPanelTitle isOpen={this.state.panelsOpen[pi]}>
                <div className={[activeProfileStyles, styles.profileTitleContainer].join(' ')}>
                  {(profileTitle && profileTitle.length >= 32) ? (
                    <ToolTip position="left" tooltipText={profileTitle}>
                      <div className={styles.profileTitleWrapper}>
                        {profileTitle}
                      </div>
                    </ToolTip>
                  ) : (
                    <div className={styles.profileTitleWrapper}>
                      {profileTitle}
                    </div>
                  )}

                  <DuplicateCodingMenu
                    visible={this.state.panelsOpen[pi] && !profile.isSubscribedDataset}
                    profileId={profile.id}
                    onSaveAndApply={this.onDuplicateCodingSubmit}
                    profiles={profiles.filter((p) => p.id !== profile.id && !p.isSubscribedDataset)}
                  />
                </div>
              </ExpansionPanelTitle>
              <ExpansionPanelBody isOpen={this.state.panelsOpen[pi]}>
                {Fields}
              </ExpansionPanelBody>
            </ExpansionPanel>
          </div>
        );
      }
    );

    const submitButton = (
      <div className={styles.submitContainer}>
        <div ref={this.submitButtonRef} tabIndex="0" className={styles.submitButton}>
          <Button buttonStyle="positive" onClick={this.sendOnSubmit}>Save</Button>
        </div>
      </div>
    );

    let accountLevelCustomAttributesSettings = null;
    if (hasAccountLevelCustomAttributesFeatureFlag) {
      accountLevelCustomAttributesSettings = (<div>
        <div className={styles.panelSettings}>
          <span
            className="panel-setting-span"
            data-placement="top"
            data-title="Show all relevant datasets from all workspaces"
          >Cross-Workspace Scoring</span>
          <ToggleSwitch
            className={styles.switch}
            id="showAllProfilesToggleSwitch"
            enabled={showAllProfiles}
            onChange={this.handleToggleProfiles}
          />
        </div>
        <div className={styles.categoryBar}>Dataset Attributes</div>
      </div>);
    }

    return (
      <div
        ref={this.panelRef}
        tabIndex="0"
        onFocus={this.handleFocus}
        onKeyDown={this.handleKeyDown}
        className={styles.container}
      >
        {accountLevelCustomAttributesSettings}
        <div
          onClick={(e) => {e.preventDefault(); e.stopPropagation();}}
        >
          {Profiles}
          {profiles.some((p) => !p.isSubscribedDataset) ? submitButton : null}
        </div>
      </div>
    );
  }
}

WorkbenchPanel.propTypes = {
  accountId: PropTypes.string,
  campaignId: PropTypes.string,
  profiles: PropTypes.array,
  customFields: PropTypes.array,
  values: PropTypes.array,
  onSubmit: PropTypes.func,
  toggleSubmitButton: PropTypes.func,
  originalSentiment: PropTypes.number,
  onClickedOutside: PropTypes.func,
  hitHighlightOn: PropTypes.bool,
  dispatcher: PropTypes.object,
  onProfileChange: PropTypes.func,
  showAllProfiles: PropTypes.bool,
  hasAccountLevelCustomAttributesFeatureFlag: PropTypes.bool
};

WorkbenchPanel.defaultProps = {
  profiles: [],
  customFields: [],
  values: [],
  showAllProfiles: false,
  hasAccountLevelCustomAttributesFeatureFlag: false,
  onSubmit: () => { /* no-op */ }
};

export default listensToClickOutside(WorkbenchPanel);
